/**
 * Asesor IA — document-level controller (Horizon-safe).
 * Root cause fix: section <script> listeners get wiped by morph/hydration;
 * this binds once on document (capture) and works with static goal markup.
 */
(function () {
  if (window.__BiohackingAsesorDocBound) return;
  window.__BiohackingAsesorDocBound = true;

  const GOAL_LABELS = {
    control_peso: 'Control de peso',
    weight_control: 'Control de peso',
    composicion_corporal: 'Composición corporal',
    longevidad_celular: 'Longevidad celular',
    recuperacion_reparacion: 'Recuperación y reparación',
    sueno: 'Calidad del sueño',
    funcion_cognitiva: 'Función cognitiva',
    piel_cabello: 'Piel y cabello',
    libido_bienestar_sexual: 'Libido y bienestar sexual',
    bienestar_general: 'Bienestar general',
    inflamacion_recuperacion: 'Soporte antiinflamatorio y recuperación',
  };

  const SUBGOAL_LABELS = {
    saciedad_apetito: 'Saciedad y control del apetito',
    grasa_visceral: 'Grasa visceral',
    antojos: 'Manejo de antojos',
    metabolismo: 'Metabolismo',
    preservar_masa_magra: 'Preservar masa magra',
    composicion_corporal: 'Composición corporal',
    sensibilidad_metabolica: 'Sensibilidad metabólica',
    fuerza: 'Fuerza',
    masa_muscular: 'Masa muscular',
    recuperacion_entrenamientos: 'Recuperación entre entrenamientos',
    rendimiento_fisico: 'Rendimiento físico',
    resistencia_muscular: 'Resistencia muscular',
    recuperacion_post_entrenamiento: 'Recuperación post-entrenamiento',
    energia_vitalidad: 'Energía y vitalidad',
    salud_mitocondrial: 'Salud mitocondrial',
    estres_oxidativo: 'Estrés oxidativo',
    recuperacion_celular: 'Recuperación celular',
    envejecimiento_saludable: 'Envejecimiento saludable',
    metabolismo_celular: 'Metabolismo celular',
    bienestar_general: 'Bienestar general',
    tendones: 'Tendones',
    ligamentos: 'Ligamentos',
    articulaciones: 'Articulaciones',
    recuperacion_muscular: 'Recuperación muscular',
    recuperacion_ejercicio: 'Recuperación después del ejercicio',
    cicatrizacion: 'Cicatrización',
    recuperacion_general: 'Recuperación general',
    conciliar_sueno: 'Conciliar el sueño',
    continuidad_sueno: 'Continuidad del sueño',
    sueno_profundo: 'Sueño profundo',
    descanso_reparador: 'Descanso reparador',
    ritmo_sueno: 'Ritmo de sueño',
    relajacion_nocturna: 'Relajación nocturna',
    memoria: 'Memoria',
    claridad_mental: 'Claridad mental',
    enfoque: 'Enfoque',
    concentracion: 'Concentración',
    rendimiento_cognitivo: 'Rendimiento cognitivo',
    bienestar_mental: 'Bienestar mental',
    lineas_expresion: 'Líneas de expresión',
    elasticidad: 'Elasticidad',
    firmeza: 'Firmeza',
    calidad_piel: 'Calidad de la piel',
    colageno: 'Colágeno',
    remodelacion_cutanea: 'Remodelación cutánea',
    apariencia_cabello: 'Apariencia del cabello',
    tono_piel: 'Tono de piel',
    textura: 'Textura',
    libido: 'Libido',
    deseo: 'Deseo',
    bienestar_sexual: 'Bienestar sexual',
    vitalidad: 'Vitalidad',
    balance_general: 'Balance general',
    energia: 'Energía',
    recuperacion: 'Recuperación',
    resistencia_estres: 'Resistencia al estrés',
    soporte_antioxidante: 'Soporte antioxidante',
    inflamacion: 'Inflamación',
    bienestar_fisico: 'Bienestar físico',
    recuperacion_entrenamiento: 'Recuperación después del entrenamiento',
  };

  const HEALTH_OPTIONS = [
    ['diabetes', 'Diabetes'],
    ['hipertension', 'Hipertensión'],
    ['enfermedad_cardiovascular', 'Enfermedad cardiovascular'],
    ['alteraciones_tiroideas', 'Alteraciones tiroideas'],
    ['antecedentes_cancer', 'Antecedentes de cáncer'],
    ['enfermedad_autoinmune', 'Enfermedad autoinmune'],
    ['enfermedad_hepatica', 'Enfermedad hepática'],
    ['enfermedad_renal', 'Enfermedad renal'],
    ['embarazo', 'Embarazo'],
    ['lactancia', 'Lactancia'],
    ['terapia_hormonal', 'Terapia hormonal'],
    ['ninguna_de_las_anteriores', 'Ninguna de las anteriores'],
    ['prefiero_no_responder', 'Prefiero no responder'],
  ];

  const MED_OPTIONS = [
    ['ninguno', 'Ninguno'],
    ['medicamentos_diabetes', 'Medicamentos para diabetes'],
    ['medicamentos_peso', 'Medicamentos para control de peso / GLP-1'],
    ['medicamentos_presion', 'Medicamentos para presión arterial'],
    ['anticoagulantes', 'Anticoagulantes'],
    ['terapia_hormonal', 'Terapia hormonal'],
    ['medicamentos_psiquiatricos', 'Medicamentos psiquiátricos'],
    ['inmunosupresores', 'Inmunosupresores'],
    ['otros', 'Otros'],
    ['prefiero_no_responder', 'Prefiero no responder'],
  ];

  const BUDGETS = [
    ['esencial', 'Esencial', '1 producto'],
    ['completo', 'Completo', 'Hasta 2 productos'],
    ['premium', 'Premium', 'Hasta 3 productos'],
    ['sin_preferencia', 'Sin preferencia', 'Muéstrame las opciones más relevantes'],
  ];

  /** @type {WeakMap<Element, any>} */
  const store = new WeakMap();

  function moneyCLP(n) {
    try {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
      }).format(Number(n) || 0);
    } catch {
      return '$' + Math.round(Number(n) || 0) + ' CLP';
    }
  }

  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getState(root) {
    let s = store.get(root);
    if (!s) {
      s = {
        ageOk: false,
        mode: '',
        step: 1,
        goals: [],
        subgoals: {},
        budget: 'sin_preferencia',
        result: null,
        chat: [],
        thinking: false,
        meta: null,
      };
      store.set(root, s);
    }
    return s;
  }

  function showError(root, msg) {
    const el = root.querySelector('[data-error]');
    if (!el) return;
    if (!msg) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = msg;
  }

  function setHidden(el, hidden) {
    if (el) el.hidden = !!hidden;
  }

  function syncGoalButtons(root, state) {
    root.querySelectorAll('[data-goal]').forEach((btn) => {
      btn.classList.toggle('is-selected', state.goals.includes(btn.getAttribute('data-goal')));
    });
  }

  function renderSubgoals(root, state) {
    const host = root.querySelector('[data-subgoals]');
    if (!host) return;
    const cards = Array.from(root.querySelectorAll('[data-goal]'));
    const selected = cards.filter((c) => state.goals.includes(c.getAttribute('data-goal')));
    if (!selected.length) {
      host.innerHTML = '';
      return;
    }
    host.innerHTML = selected
      .map((card) => {
        const id = card.getAttribute('data-goal');
        const title =
          GOAL_LABELS[id] ||
          card.getAttribute('data-goal-label') ||
          card.querySelector('strong')?.textContent?.trim() ||
          'Objetivo';
        const subs = (card.getAttribute('data-subgoals') || '').split(',').filter(Boolean);
        const picked = state.subgoals[id] || [];
        const chips = subs
          .map((sg) => {
            const on = picked.includes(sg);
            return `<button type="button" class="asesor-ia__chip ${on ? 'is-selected' : ''}" data-sg-goal="${esc(id)}" data-sg="${esc(sg)}">${esc(SUBGOAL_LABELS[sg] || sg)}</button>`;
          })
          .join('');
        return `<div class="asesor-ia__subgoal-block"><h4>${esc(title)} — ¿qué te interesa más?</h4><div class="asesor-ia__chips">${chips}</div></div>`;
      })
      .join('');
  }

  function ensureSafety(root, state) {
    const budgetHost = root.querySelector('[data-budgets]');
    if (budgetHost && !budgetHost.dataset.ready) {
      budgetHost.dataset.ready = '1';
      budgetHost.innerHTML = BUDGETS.map(([id, label, desc]) => {
        const on = state.budget === id;
        return `<button type="button" class="asesor-ia__card ${on ? 'is-selected' : ''}" data-budget="${id}"><strong>${esc(label)}</strong><span>${esc(desc)}</span></button>`;
      }).join('');
    }
    const healthHost = root.querySelector('[data-health]');
    if (healthHost && !healthHost.dataset.ready) {
      healthHost.dataset.ready = '1';
      healthHost.innerHTML = HEALTH_OPTIONS.map(
        ([id, label]) =>
          `<label class="asesor-ia__check"><input type="checkbox" value="${id}" data-health-cb /> ${esc(label)}</label>`
      ).join('');
    }
    const medHost = root.querySelector('[data-meds]');
    if (medHost && !medHost.dataset.ready) {
      medHost.dataset.ready = '1';
      medHost.innerHTML = MED_OPTIONS.map(
        ([id, label]) =>
          `<label class="asesor-ia__check"><input type="checkbox" value="${id}" data-med-cb /> ${esc(label)}</label>`
      ).join('');
    }
  }

  function render(root) {
    const state = getState(root);
    showError(root, '');
    const age = root.querySelector('[data-panel="age"]');
    const quiz = root.querySelector('[data-panel="quiz"]');
    const chat = root.querySelector('[data-panel="chat"]');
    const result = root.querySelector('[data-panel="result"]');
    const modes = root.querySelector('[data-modes]');

    if (!state.ageOk) {
      setHidden(age, false);
      setHidden(quiz, true);
      setHidden(chat, true);
      setHidden(result, true);
      setHidden(modes, true);
      return;
    }

    setHidden(age, true);
    setHidden(modes, false);
    root.querySelectorAll('[data-mode]').forEach((b) => {
      b.setAttribute('aria-selected', state.mode && b.getAttribute('data-mode') === state.mode ? 'true' : 'false');
    });

    if (!state.mode) {
      setHidden(quiz, true);
      setHidden(chat, true);
      setHidden(result, true);
      return;
    }

    if (state.mode === 'chat') {
      setHidden(quiz, true);
      setHidden(result, true);
      setHidden(chat, false);
      renderChat(root, state);
      return;
    }

    setHidden(chat, true);
    if (state.step === 4 && state.result) {
      setHidden(quiz, true);
      setHidden(result, false);
      renderResult(root, state);
      return;
    }

    setHidden(result, true);
    setHidden(quiz, false);
    root.querySelectorAll('[data-step]').forEach((el) => {
      el.hidden = Number(el.getAttribute('data-step')) !== state.step;
    });
    const next = root.querySelector('[data-next]');
    const back = root.querySelector('[data-back]');
    if (next) next.textContent = state.step === 3 ? 'Ver mi selección' : 'Continuar';
    if (back) back.hidden = state.step <= 1;
    syncGoalButtons(root, state);
    if (state.step === 1) renderSubgoals(root, state);
    if (state.step === 3) ensureSafety(root, state);
  }

  function collectProfile(root) {
    const get = (name) => root.querySelector(`[name="${name}"]`)?.value || '';
    return {
      age_range: get('age_range'),
      sex: get('sex'),
      activity: get('activity'),
      sleep: get('sleep'),
      stress: get('stress'),
      experience: get('experience'),
      route_preference: get('route_preference') || 'sin_preferencia',
      horizon_weeks: Number(get('horizon_weeks') || 8),
      priority: get('priority') || 'balanceado',
      improve_text: get('improve_text'),
    };
  }

  async function api(proxyUrl, path, body) {
    const base = (proxyUrl || '').replace(/\/+$/, '');
    const res = await fetch(base + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Error ' + res.status);
    return data;
  }

  async function submitAdvise(root, state) {
    const proxyUrl = root.getAttribute('data-proxy-url') || '';
    const loading = root.querySelector('[data-loading]');
    const profile = collectProfile(root);
    const health = Array.from(root.querySelectorAll('[data-health-cb]:checked')).map((el) => el.value);
    const meds = Array.from(root.querySelectorAll('[data-med-cb]:checked')).map((el) => el.value);
    const flatSubs = Object.values(state.subgoals).flat();

    setHidden(root.querySelector('[data-panel="quiz"]'), true);
    setHidden(loading, false);

    try {
      const data = await api(proxyUrl, '/advise', {
        answers: {
          age_confirmed: true,
          goals: state.goals,
          subgoals: flatSubs,
          budget: state.budget,
          route_preference: profile.route_preference,
          horizon_weeks: profile.horizon_weeks,
          priority_declared: profile.priority,
          experience: profile.experience,
          age_range: profile.age_range,
          sex: profile.sex,
          activity: profile.activity,
          sleep: profile.sleep,
          stress: profile.stress,
          improve_text: profile.improve_text,
          health_conditions: health,
          medications: meds,
        },
      });
      state.result = data;
      state.step = 4;
      setHidden(loading, true);
      render(root);
    } catch (e) {
      setHidden(loading, true);
      setHidden(root.querySelector('[data-panel="quiz"]'), false);
      showError(root, e.message || 'No se pudo generar el resultado.');
      render(root);
    }
  }

  function renderResult(root, state) {
    const host = root.querySelector('[data-result-body]');
    if (!host || !state.result) return;
    const r = state.result;
    const warning = r.global_warning || root.getAttribute('data-disclaimer') || '';
    const products = r.products || [];
    const affinity =
      r.safety_status === 'CLEAR' && r.match_score
        ? `<div class="asesor-ia__affinity"><b>${esc(String(r.match_score))}%</b> Coincidencia con tus objetivos</div>`
        : '';
    const summary =
      r.summary && r.safety_status === 'CLEAR'
        ? `<p class="asesor-ia__meta">${esc(String(r.summary.product_count))} producto(s) · ${esc(String(r.summary.horizon_weeks))} semanas · ${esc(moneyCLP(r.summary.approx_cost))}</p>`
        : '';
    const cards = products
      .map((p) => {
        const img = p.image_url
          ? `<img src="${esc(p.image_url)}" alt="" width="88" height="88" loading="lazy" />`
          : '<div style="width:88px;height:88px;border-radius:10px;background:#eee"></div>';
        return `<article class="asesor-ia__product">
          ${img}
          <div class="asesor-ia__product-body">
            <span class="asesor-ia__badge">${esc(p.role || 'Opción')}</span>
            <h3>${esc(p.name)}</h3>
            <p>${esc(p.presentation || '')}</p>
            <p>${esc(p.reason || '')}</p>
            <p class="asesor-ia__price">${esc(moneyCLP(p.price))} · Stock: ${esc(String(p.stock ?? '—'))}</p>
            <div class="asesor-ia__product-actions">
              <a class="asesor-ia__btn asesor-ia__btn--soft" href="${esc(p.product_url || '#')}">Ver producto</a>
              <button type="button" class="asesor-ia__btn asesor-ia__btn--primary" data-add-variant="${esc(p.variant_id || '')}">Agregar al carrito</button>
            </div>
          </div>
        </article>`;
      })
      .join('');

    host.innerHTML = `
      <div class="asesor-ia__result-head">
        ${affinity}
        <h2 class="asesor-ia__step-title">${esc(r.title || 'Resultado')}</h2>
        ${r.subtitle ? `<p class="asesor-ia__step-copy">${esc(r.subtitle)}</p>` : ''}
      </div>
      <div class="asesor-ia__warn"><strong>Importante</strong>${esc(warning)}</div>
      ${summary}
      ${r.why_summary || r.message ? `<p class="asesor-ia__step-copy">${esc(r.why_summary || r.message)}</p>` : ''}
      <div class="asesor-ia__products">${cards || '<p class="asesor-ia__meta">Sin productos en este resultado.</p>'}</div>
      <div class="asesor-ia__actions" style="justify-content:flex-start;margin-top:1.5rem">
        <button type="button" class="asesor-ia__btn asesor-ia__btn--ghost" data-restart>Reiniciar asesor</button>
      </div>`;
  }

  function formatBotHtml(text) {
    let s = esc(text);
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a class="asesor-ia__link" href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    s = s.replace(/(^|[\s>])(https?:\/\/[^\s<]+)/g, '$1<a class="asesor-ia__link" href="$2" target="_blank" rel="noopener noreferrer">$2</a>');
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return s;
  }

  function productCardsHtml(products) {
    if (!products || !products.length) return '';
    return `<div class="asesor-ia__products asesor-ia__products--chat">${products
      .map((p) => {
        const img = p.image_url
          ? `<img src="${esc(p.image_url)}" alt="" width="72" height="72" loading="lazy" />`
          : '<div style="width:72px;height:72px;border-radius:10px;background:#eee"></div>';
        const price = p.price_display || moneyCLP(p.price);
        const oos = p.in_stock === false || Number(p.stock) <= 0;
        return `<article class="asesor-ia__product">
          ${img}
          <div class="asesor-ia__product-body">
            <h3>${esc(p.name)}</h3>
            <p>${esc(p.presentation || '')}</p>
            <p class="asesor-ia__price">${esc(price)}${oos ? ' · <span class="asesor-ia__oos">Sin stock</span>' : p.stock != null ? ` · Stock: ${esc(String(p.stock))}` : ''}</p>
            <div class="asesor-ia__product-actions">
              <a class="asesor-ia__btn asesor-ia__btn--soft" href="${esc(p.product_url || '#')}" target="_blank" rel="noopener noreferrer">Ver producto</a>
              ${
                !oos && p.variant_id
                  ? `<button type="button" class="asesor-ia__btn asesor-ia__btn--primary" data-add-variant="${esc(p.variant_id)}" translate="no">Añadir al carrito</button>`
                  : oos
                    ? `<span class="asesor-ia__oos-pill" translate="no">Agotado</span>`
                    : ''
              }
            </div>
          </div>
        </article>`;
      })
      .join('')}</div>`;
  }

  function suggestList(root) {
    const defaults = [
      '¿Qué productos tienen relacionados con recuperación?',
      '¿Qué opciones hay para piel y colágeno?',
      'Cuéntame sobre BPC-157 en el catálogo',
      '¿Qué productos hay disponibles ahora?',
    ];
    return [1, 2, 3, 4].map((n, i) => root.getAttribute(`data-suggest-${n}`) || defaults[i]);
  }

  function emptyChatHtml(root) {
    const [s1, s2, s3, s4] = suggestList(root);
    const bolt =
      '<span class="asesor-ia__suggest-bolt" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M13 2 4 14h7l-1 8 10-14h-7l0-6Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg></span>';
    return `<div class="asesor-ia__chat-empty">
      <div class="asesor-ia__chat-empty-icon" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M8 4c2.5 2 2.5 6 0 8 2.5 2 2.5 6 0 8M16 4c-2.5 2-2.5 6 0 8-2.5 2-2.5 6 0 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="8" r="1.2" fill="currentColor"/><circle cx="12" cy="16" r="1.2" fill="currentColor"/></svg>
      </div>
      <h3>Pregúntame sobre péptidos de investigación</h3>
      <p>Respondo con información educativa y el catálogo de BioHacking Peptidos. Solo con fines de investigación.</p>
      <div class="asesor-ia__suggest-wrap">
        <div class="asesor-ia__suggest-group">
          <h4>Catálogo</h4>
          <div class="asesor-ia__suggest-grid">
            <button type="button" class="asesor-ia__suggest" data-suggest="${esc(s1)}">${bolt}<span>${esc(s1)}</span></button>
            <button type="button" class="asesor-ia__suggest" data-suggest="${esc(s2)}">${bolt}<span>${esc(s2)}</span></button>
          </div>
        </div>
        <div class="asesor-ia__suggest-group">
          <h4>Productos</h4>
          <div class="asesor-ia__suggest-grid">
            <button type="button" class="asesor-ia__suggest" data-suggest="${esc(s3)}">${bolt}<span>${esc(s3)}</span></button>
            <button type="button" class="asesor-ia__suggest" data-suggest="${esc(s4)}">${bolt}<span>${esc(s4)}</span></button>
          </div>
        </div>
      </div>
    </div>`;
  }

  function thinkingHtml() {
    return `<div class="asesor-ia__bubble asesor-ia__bubble--bot asesor-ia__bubble--thinking" data-thinking>
      <span class="asesor-ia__dots" aria-hidden="true"><i></i><i></i><i></i></span>
      <span>Pensando…</span>
    </div>`;
  }

  function renderChat(root, state) {
    const log = root.querySelector('[data-chat-log]');
    const form = root.querySelector('[data-chat-form]');
    const sendBtn = form?.querySelector('.asesor-ia__send');
    const input = root.querySelector('[data-chat-input]');
    if (!log) return;

    if (!state.chat.length && !state.thinking) {
      log.innerHTML = emptyChatHtml(root);
      if (sendBtn) sendBtn.disabled = false;
      if (input) input.disabled = false;
      return;
    }

    const parts = state.chat.map((m) => {
      if (m.role === 'user') {
        return `<div class="asesor-ia__bubble asesor-ia__bubble--user">${esc(m.content)}</div>`;
      }
      return `<div class="asesor-ia__chat-block">
        <div class="asesor-ia__bubble asesor-ia__bubble--bot">${formatBotHtml(m.content)}</div>
        ${productCardsHtml(m.products)}
      </div>`;
    });
    if (state.thinking) parts.push(thinkingHtml());
    log.innerHTML = parts.join('');
    log.scrollTop = log.scrollHeight;
    if (sendBtn) sendBtn.disabled = !!state.thinking;
    if (input) input.disabled = !!state.thinking;
  }

  async function sendChat(root, state, text) {
    const msg = String(text || '').trim();
    if (!msg || state.thinking) return;
    state.chat.push({ role: 'user', content: msg });
    state.thinking = true;
    renderChat(root, state);
    const input = root.querySelector('[data-chat-input]');
    if (input) input.value = '';
    try {
      const history = state.chat
        .filter((m) => m.content)
        .map((m) => ({
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
        }));
      const data = await api(root.getAttribute('data-proxy-url') || '', '/chat', {
        messages: history,
      });
      state.chat.push({
        role: 'assistant',
        content: data.reply || 'Sin respuesta',
        products: Array.isArray(data.products) ? data.products : [],
      });
    } catch (e) {
      state.chat.push({ role: 'assistant', content: e.message || 'Error' });
    }
    state.thinking = false;
    renderChat(root, state);
  }

  async function addToCart(root, variantId, btn) {
    if (!variantId) return;
    const label = btn?.textContent;
    try {
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Validando…';
      }
      await api(root.getAttribute('data-proxy-url') || '', '/tools/validate_stock', {
        variant_id: variantId,
      });
      const numeric = String(variantId).includes('/')
        ? String(variantId).split('/').pop()
        : String(variantId);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(numeric), quantity: 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'No se pudo agregar');
      if (btn) btn.textContent = 'Agregado';
    } catch (e) {
      showError(root, e.message || 'Error al agregar');
      if (btn) {
        btn.disabled = false;
        btn.textContent = label || 'Agregar al carrito';
      }
    }
  }

  function onClick(e) {
    const t = e.target.closest(
      '[data-age-yes],[data-age-no],[data-mode],[data-next],[data-back],[data-goal],[data-sg],[data-budget],[data-restart],[data-add-variant],[data-suggest]'
    );
    if (!t) return;
    const root = t.closest('[data-asesor-ia]');
    if (!root) return;

    // Prevent Theme Editor from only selecting when possible
    e.preventDefault();
    e.stopPropagation();

    const state = getState(root);

    if (t.hasAttribute('data-suggest')) {
      if (!state.ageOk || state.thinking) return;
      sendChat(root, state, t.getAttribute('data-suggest'));
      return;
    }

    if (t.hasAttribute('data-age-yes')) {
      state.ageOk = true;
      state.step = 1;
      state.mode = '';
      root.setAttribute('data-age-ok', '1');
      render(root);
      return;
    }
    if (t.hasAttribute('data-age-no')) {
      const msg = root.querySelector('[data-age-blocked]');
      if (msg) {
        msg.hidden = false;
        msg.textContent =
          'Este asesor y los productos presentados están destinados exclusivamente a personas mayores de 18 años. No podemos generar sugerencias para menores de edad.';
      }
      return;
    }
    if (t.hasAttribute('data-mode')) {
      if (!state.ageOk) return;
      state.mode = t.getAttribute('data-mode') === 'chat' ? 'chat' : 'quiz';
      render(root);
      return;
    }
    if (t.hasAttribute('data-goal')) {
      const id = t.getAttribute('data-goal');
      if (state.goals.includes(id)) {
        state.goals = state.goals.filter((x) => x !== id);
        delete state.subgoals[id];
      } else if (state.goals.length < 3) {
        state.goals.push(id);
      } else {
        showError(root, 'Máximo 3 objetivos principales.');
        return;
      }
      syncGoalButtons(root, state);
      renderSubgoals(root, state);
      return;
    }
    if (t.hasAttribute('data-sg')) {
      const g = t.getAttribute('data-sg-goal');
      const sg = t.getAttribute('data-sg');
      const list = new Set(state.subgoals[g] || []);
      if (list.has(sg)) list.delete(sg);
      else list.add(sg);
      state.subgoals[g] = [...list];
      renderSubgoals(root, state);
      return;
    }
    if (t.hasAttribute('data-budget')) {
      state.budget = t.getAttribute('data-budget');
      root.querySelectorAll('[data-budget]').forEach((b) => {
        b.classList.toggle('is-selected', b.getAttribute('data-budget') === state.budget);
      });
      return;
    }
    if (t.hasAttribute('data-next')) {
      if (state.step === 1) {
        if (!state.goals.length) {
          showError(root, 'Selecciona al menos un objetivo.');
          return;
        }
        state.step = 2;
        render(root);
        return;
      }
      if (state.step === 2) {
        state.step = 3;
        render(root);
        return;
      }
      if (state.step === 3) {
        submitAdvise(root, state);
      }
      return;
    }
    if (t.hasAttribute('data-back')) {
      if (state.step > 1 && state.step < 4) {
        state.step -= 1;
        render(root);
      }
      return;
    }
    if (t.hasAttribute('data-restart')) {
      state.step = 1;
      state.goals = [];
      state.subgoals = {};
      state.result = null;
      state.mode = 'quiz';
      const bh = root.querySelector('[data-budgets]');
      const hh = root.querySelector('[data-health]');
      const mh = root.querySelector('[data-meds]');
      if (bh) delete bh.dataset.ready;
      if (hh) delete hh.dataset.ready;
      if (mh) delete mh.dataset.ready;
      render(root);
      return;
    }
    if (t.hasAttribute('data-add-variant')) {
      addToCart(root, t.getAttribute('data-add-variant'), t);
    }
  }

  function onSubmit(e) {
    const form = e.target.closest('[data-chat-form]');
    if (!form) return;
    const root = form.closest('[data-asesor-ia]');
    if (!root) return;
    e.preventDefault();
    const state = getState(root);
    if (!state.ageOk) {
      render(root);
      return;
    }
    const input = root.querySelector('[data-chat-input]');
    sendChat(root, state, input?.value);
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);

  function bootAll() {
    document.querySelectorAll('[data-asesor-ia]').forEach((root) => {
      root.setAttribute('data-asesor-js', '1');
      const state = getState(root);
      root.setAttribute('data-age-ok', '0');
      state.ageOk = false;
      state.mode = '';
      render(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();
  document.addEventListener('shopify:section:load', bootAll);

  window.BiohackingAsesorIA = {
    boot: bootAll,
    ageYes: function (btn) {
      const root = btn.closest('[data-asesor-ia]');
      if (!root) return false;
      const state = getState(root);
      state.ageOk = true;
      state.step = 1;
      state.mode = '';
      root.setAttribute('data-age-ok', '1');
      render(root);
      return false;
    },
    ageNo: function (btn) {
      const root = btn.closest('[data-asesor-ia]');
      const msg = root?.querySelector('[data-age-blocked]');
      if (msg) {
        msg.hidden = false;
        msg.textContent =
          'Este asesor y los productos presentados van destinados exclusivamente a personas mayores de 18 años.';
      }
      return false;
    },
    mode: function (btn, mode) {
      const root = btn.closest('[data-asesor-ia]');
      if (!root) return false;
      const state = getState(root);
      if (!state.ageOk) return false;
      state.mode = mode === 'chat' ? 'chat' : 'quiz';
      render(root);
      return false;
    },
    next: function (btn) {
      const root = btn.closest('[data-asesor-ia]');
      if (!root) return false;
      onClick({ target: root.querySelector('[data-next]'), preventDefault() {}, stopPropagation() {} });
      return false;
    },
    back: function (btn) {
      const root = btn.closest('[data-asesor-ia]');
      if (!root) return false;
      onClick({ target: root.querySelector('[data-back]'), preventDefault() {}, stopPropagation() {} });
      return false;
    },
  };
})();
